import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    SimpleChanges,
    ViewChild,
} from '@angular/core'
import { TimeSlotData } from '@app/core/_services/available-times.service'
import { AvailablePartnerTileComponent } from '../available-partner-tile/available-partner-tile.component'
import {
    FmWindowResizedDirective,
    WindowResizedType,
} from '@app/shared/directives/fm-window-resized.directive'
import {
    FIFTEEN_MINS_IN_MSECS,
    SelectedSession,
    SelectedSessionService,
    UtilsService,
} from '@app/core/_services'
import { ConfirmedSessionsResponse, SESSION_DURATION_25_MINS, TimeFormat } from '@app/core/_models'
import { SessionSelectorOutlineComponent } from '../session-selector-outline/session-selector-outline.component'
import { SelectedSessionTileComponent } from '../selected-session-tile/selected-session-tile.component'
import { MS_IN_ONE_DAY } from '@app/core/_services/time-utilities.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { BookedSessionTileComponent } from '../booked-session-tile/booked-session-tile.component'
import { EpochToTimePipe } from '@app/core/pipes/epoch-to-time.pipe'

export const TIMESLOT_HEIGHT = 48 // Each 15minutes slot is 48px high
// 24hours, 4 slots per hour (:00,:15,:30,:45) + 3 slots
// at the end in case a session tile needs to be placed
// at the end of the day
export const NUM_TIMESLOTS_PER_DAY = 24 * 4 + 3

export interface CalendarDayAvailablePartner extends TimeSlotData {
    sessionTime: number
    sessionDuration: number
    isSelectable?: boolean
}

export interface CalendarDayData {
    startOfDay: number
    potentialPartnerData: Array<CalendarDayAvailablePartner>
    bookedSessionData: Array<ConfirmedSessionsResponse>
    selectedSessionData: Array<SelectedSession>
}

@Component({
    selector: 'fm-calendar-day',
    imports: [
        EpochToTimePipe,
        CommonModule,
        AvailablePartnerTileComponent,
        FmWindowResizedDirective,
        SessionSelectorOutlineComponent,
        SelectedSessionTileComponent,
        BookedSessionTileComponent,
    ],
    templateUrl: './calendar-day.component.html',
    styleUrl: './calendar-day.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarDayComponent {
    @Input() isToday: boolean
    @Input() dayData: CalendarDayData
    @Input() timezone: string
    @Input() timeFormat: TimeFormat = '12hr'
    @Input() sessionDuration: number = SESSION_DURATION_25_MINS
    @ViewChild('dayContainer', { read: ElementRef, static: true }) dayContainer: ElementRef

    public fifteenMinsInMs = FIFTEEN_MINS_IN_MSECS
    public endOfDay: number
    public timeslots = Array.from({ length: NUM_TIMESLOTS_PER_DAY }, (_, index) => index)

    // Remove data the is covered up by confirmed or selected sessions
    // to avoid the need for z-index and better memory efficiency
    public filteredPotentialPartnerData: Array<CalendarDayAvailablePartner>

    // Determine which timeslot is being pointed to
    // and place session outline accordingly
    pointerYCoordinate: number | null = null

    // Session outline config variables
    sessionOutlineTop: number | null = null // Default to null when not hovering
    dayColumnWidth: number | null = null
    slotNumber: number | null = null
    pointedAtSessionTime: number | null = null
    pointedAtSessionInfo: CalendarDayAvailablePartner | null = null

    private unavailableRanges: Array<[number, number]>

    // Time line config
    public timeLineTop: string
    public timeLineAnchorTop: string
    public currentTimeInMs: number
    public displayTimeFormat: string

    constructor(
        private cdr: ChangeDetectorRef,
        private selectedSessionService: SelectedSessionService,
        private utilsService: UtilsService,
        private sessionSettingsService: SessionSettingsService,
    ) {}

    ngOnInit() {
        this.endOfDay = this.dayData.startOfDay + MS_IN_ONE_DAY
        this.updateUnavailableRanges()
        this.filterPotentialPartnerData()

        // In the case where a slot is selected or booked,
        // make sure we remove the outline
        this.updateRectanglePosition(true)
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.dayContainer) {
            // Ensure wdith is set correctly if number of days changes
            setTimeout(() => {
                this.dayColumnWidth = this.dayContainer.nativeElement.offsetWidth
                this.cdr.detectChanges()
            }, 0)
            this.setTimeLineIndicatorPosition()
        }
        this.updateUnavailableRanges()
        this.filterPotentialPartnerData()
        // In the case where a slot is selected or booked,
        // make sure we remove the outline
        this.updateRectanglePosition(true)
    }

    updateUnavailableRanges() {
        this.unavailableRanges = new Array<[number, number]>(0)
        for (const session of this.dayData.selectedSessionData) {
            this.unavailableRanges.push([
                session.sessionTime,
                session.sessionTime + session.sessionDuration,
            ])
        }
        if (this.dayData.bookedSessionData) {
            for (const session of this.dayData.bookedSessionData) {
                this.unavailableRanges.push([
                    session.sessionTime,
                    session.sessionTime + session.duration,
                ])
            }
        }
    }

    private filterPotentialPartnerData(): void {
        const localPotentialPartnerData = []

        for (const potentialPartner of this.dayData.potentialPartnerData) {
            if (this.isSlotCoveredByConfirmedOrSelectedSession(potentialPartner.sessionTime)) {
                localPotentialPartnerData.push(potentialPartner)
            }
        }

        this.filteredPotentialPartnerData = [...localPotentialPartnerData]
    }

    onWindowResized(viewport: WindowResizedType) {
        this.dayColumnWidth = viewport.width
    }

    onMouseMove(event: MouseEvent) {
        // Calculate the Y position relative to the top of the scrollable container
        this.pointerYCoordinate =
            event.clientY -
            this.dayContainer.nativeElement.getBoundingClientRect().top +
            this.dayContainer.nativeElement.scrollTop

        this.updateRectanglePosition()
    }

    onMouseLeave() {
        this.sessionOutlineTop = null // Don't show session outline
        this.pointerYCoordinate = null // Reset Y coordinate position
    }

    updateRectanglePosition(removeOnly: boolean = false) {
        if (this.pointerYCoordinate !== null && this.dayContainer) {
            const top =
                Math.floor(this.pointerYCoordinate / TIMESLOT_HEIGHT) * TIMESLOT_HEIGHT -
                this.dayContainer.nativeElement.scrollTop
            // Determine which session timeslot is pointed at
            this.slotNumber = Math.floor(top / TIMESLOT_HEIGHT)
            this.pointedAtSessionTime =
                this.slotNumber * FIFTEEN_MINS_IN_MSECS + this.dayData.startOfDay
            this.pointedAtSessionInfo = this.getPotentialPartnerInfo(this.pointedAtSessionTime)

            if (this.timeslotIsSelectable(this.pointedAtSessionTime, this.slotNumber)) {
                if (!removeOnly) {
                    this.sessionOutlineTop = top
                }
            } else {
                this.sessionOutlineTop = null
            }
        }
    }

    timeslotIsSelectable(sessionTime: number, timeSlotNumber: number): boolean {
        if (sessionTime > this.utilsService.getCurrentTimestamp()) {
            if (timeSlotNumber >= 96) {
                return false
            }
            return this.canScheduleMeeting(sessionTime, this.sessionDuration)
        }
        return false
    }

    private canScheduleMeeting(
        startTime: number, // Meeting start time in milliseconds
        duration: number, // Meeting duration in milliseconds
    ): boolean {
        const meetingEndTime = startTime + duration
        const unavailableRanges = this.unavailableRanges

        if (this.unavailableRanges) {
            for (const [unavailableStart, unavailableEnd] of unavailableRanges) {
                // Check if the meeting overlaps with the current unavailable range
                if (startTime <= unavailableEnd && meetingEndTime >= unavailableStart) {
                    // The meeting overlaps with an unavailable time slot
                    return false
                }
            }
        }

        // No overlap found, meeting can be scheduled
        return true
    }

    private isSlotCoveredByConfirmedOrSelectedSession(sessionTIme: number): boolean {
        const unavailableRanges = this.unavailableRanges

        if (this.unavailableRanges) {
            for (const [unavailableStart, unavailableEnd] of unavailableRanges) {
                // Check if the meeting overlaps with the current unavailable range
                if (sessionTIme <= unavailableEnd && sessionTIme >= unavailableStart) {
                    // The meeting overlaps with an unavailable time slot
                    return false
                }
            }
        }

        // No overlap found
        return true
    }

    public onClickSelectSlot(event): void {
        const pointerYCoordinate =
            event.clientY -
            this.dayContainer.nativeElement.getBoundingClientRect().top +
            this.dayContainer.nativeElement.scrollTop

        const timeSlot = Math.floor(pointerYCoordinate / TIMESLOT_HEIGHT)
        const sessionTime = timeSlot * FIFTEEN_MINS_IN_MSECS + this.dayData.startOfDay

        if (this.timeslotIsSelectable(sessionTime, timeSlot)) {
            const sessionPreferenceFavorites =
                this.sessionSettingsService.getSessionSettingFavoritesPreference()
            const sessionActivityType = this.sessionSettingsService.getSessionSettingActivityType()
            const sessionQuietMode = this.sessionSettingsService.quietModeEnabled()

            const partnerData = this.getPotentialPartnerInfo(sessionTime)

            this.selectedSessionService.addSelectedSession({
                sessionDuration: this.sessionDuration,
                sessionTime: sessionTime,
                userAvailabilityCode: '',
                sessionPreferenceFavorites: sessionPreferenceFavorites,
                activityType: sessionActivityType,
                quietMode: sessionQuietMode,
                user: partnerData?.partner?.displayName ?? '',
                profileUrl: partnerData?.partner?.profileSlug ?? '',
            })
        }
    }

    private getPotentialPartnerInfo(sessionTime: number): CalendarDayAvailablePartner | null {
        for (const session of this.dayData.potentialPartnerData) {
            if (session.sessionTime === sessionTime) {
                return session
            }
        }
        return null
    }

    private setTimeLineIndicatorPosition() {
        if (this.timeFormat == '12hr') {
            this.displayTimeFormat = 'h:mma'
        } else {
            this.displayTimeFormat = 'HH:mm'
        }
        let now = this.utilsService.getCurrentTimeForTimezone(this.timezone)
        this.currentTimeInMs = this.utilsService.getCurrentTimestamp()
        let hour = now.hour()
        let top = hour * (TIMESLOT_HEIGHT * 4) + (now.minute() / 60) * (TIMESLOT_HEIGHT * 4)

        this.timeLineTop = top.toString() + 'px'
        this.timeLineAnchorTop = (top - 5).toString() + 'px'
    }
}
